// src/lib/websocket.ts
 
import { apiClient } from './api-client';
 
export type WsStatus = 'connecting' | 'live' | 'offline';
 
type EventCallback = (payload: any) => void;
type StatusCallback = (status: WsStatus) => void;
 
class RealtimeManager {
  private ws: WebSocket | null = null;
 
  private listeners = new Map<string, Set<EventCallback>>();
  private statusListeners = new Set<StatusCallback>();
 
  private reconnectTimer: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
 
  private intentionalClose = false;
  private connecting = false;
  private validating = false;
 
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
 
  private lastPong = Date.now();
 
  private _status: WsStatus = 'offline';
 
  // Which endpoint we're connected to — '/ws' for staff/client, '/ws/customer'
  // for the read-only customer role. Remembered so reconnects (which call
  // connect() with no args) reuse whatever path the caller originally chose.
  private wsPath: string = '/ws';
 
  get status(): WsStatus {
    return this._status;
  }
 
  private setStatus(status: WsStatus) {
    this._status = status;
 
    this.statusListeners.forEach((cb) => cb(status));
  }
 
  onStatusChange(cb: StatusCallback) {
    this.statusListeners.add(cb);
 
    cb(this._status);
 
    return () => {
      this.statusListeners.delete(cb);
    };
  }
 
 async connect(path: string = this.wsPath) {
  if (typeof window === 'undefined') return;
 
  // Switching endpoints (e.g. role changed) — drop the old socket first so
  // we don't end up joined to the wrong room.
  if (this.ws && path !== this.wsPath) {
    this.intentionalClose = true;
    this.ws.close();
    this.ws = null;
    this.intentionalClose = false;
  }
 
  this.wsPath = path;
 
  if (this.connecting) return;
  if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
    return;
  }
 
  this.connecting = true;
  this.intentionalClose = false;
  this.setStatus('connecting');
 
  try {
    if (!this.validating) {
      this.validating = true;
      try {
        await apiClient.get('/auth/me'); // confirms a valid session exists before opening WS
      } catch (err) {
        console.error('WS auth validation failed', err);
        this.setStatus('offline');
        this.validating = false;
        this.connecting = false;
        return;
      }
      this.validating = false;
    }
 
    const wsBase = process.env.NEXT_PUBLIC_WS_URL || 'ws://localhost:8000';
    this.ws = new WebSocket(`${wsBase}${this.wsPath}`);   // no ?token= — cookie authenticates automatically
 
    // ...everything else (onopen, onmessage, onerror, onclose) unchanged
 
      this.ws.onopen = () => {
        console.log('Realtime connected');
 
        this.reconnectAttempts = 0;
 
        this.setStatus('live');
 
        this.startPing();
 
        this.connecting = false;
      };
 
      this.ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
 
          if (payload.event === 'pong') {
            this.lastPong = Date.now();
            return;
          }
 
          const eventType = payload.event;
          const data = payload.data;
 
          if (!eventType) return;
 
          this.emit(eventType, data);
 
          const namespace = eventType.split('.')[0];
 
          this.emit(namespace, data);
 
        } catch (err) {
          console.error('WS parse error', err);
        }
      };
 
      this.ws.onerror = (err) => {
        console.error('WS error', err);
 
        this.setStatus('offline');
      };
 
      this.ws.onclose = (event) => {
        console.log('WS closed', event.code);
 
        this.stopPing();
 
        this.setStatus('offline');
 
        this.connecting = false;
 
        this.ws = null;
 
        if (
          event.code === 4001 ||
          event.code === 4002 ||
          event.code === 4003
        ) {
          return;
        }
 
        if (!this.intentionalClose) {
          this.scheduleReconnect();
        }
      };
 
    } catch (err) {
      console.error(err);
 
      this.connecting = false;
 
      this.setStatus('offline');
    }
  }
 
  disconnect() {
    this.intentionalClose = true;
 
    this.stopPing();
 
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
 
    this.ws?.close();
 
    this.ws = null;
 
    this.setStatus('offline');
  }
 
  subscribe(channels: string[]) {
    this.send('subscribe', { channels });
  }
 
  unsubscribe(channels: string[]) {
    this.send('unsubscribe', { channels });
  }
 
  on(event: string, callback: EventCallback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
 
    this.listeners.get(event)!.add(callback);
  }
 
  off(event: string, callback: EventCallback) {
    this.listeners.get(event)?.delete(callback);
  }
 
  private emit(event: string, payload: any) {
    this.listeners.get(event)?.forEach((cb) => {
      cb(payload);
    });
  }
 
  private send(action: string, payload?: any) {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
 
    this.ws.send(
      JSON.stringify({
        action,
        ...payload,
      })
    );
  }
 
  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }
 
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
 
    this.reconnectAttempts++;
 
    const timeout =
      Math.min(3000 * this.reconnectAttempts, 15000);
 
    this.reconnectTimer = setTimeout(() => {
      this.connect(this.wsPath);
    }, timeout);
  }
 
  private startPing() {
    this.stopPing();
 
    this.lastPong = Date.now();
 
    this.pingInterval = setInterval(() => {
      if (Date.now() - this.lastPong > 60000) {
        console.warn('WS heartbeat timeout');
 
        this.ws?.close();
 
        return;
      }
 
      this.send('ping');
    }, 30000);
  }
 
  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
 
      this.pingInterval = null;
    }
  }
}
 
export const realtime = new RealtimeManager();
 